// Standalone auth configuration (ENG-1166), read from Vite build env.
//
// The standalone SPA authenticates against a Cognito Hosted UI using the
// authorization-code + PKCE grant (public client, no secret). Everything here
// is PUBLIC config — a client id and Hosted-UI URLs are not secrets — so it is
// safe to inline into the browser bundle.
//
// DEV DEFAULTS below are intentionally non-functional PLACEHOLDERS: local dev
// verifies the flow through the `?demo=auth` mock harness, which never touches a
// real Cognito. A deployment MUST override every VITE_AUTH_* var with the real
// pool's values (see docs/contracts/orovia-auth-v1.json → deployConfigGated).

export interface AuthConfig {
  /** Hosted-UI origin, e.g. https://orovia-prod.auth.us-east-1.amazoncognito.com */
  authDomain: string
  /** App client id (public SPA client). */
  clientId: string
  /** OAuth redirect_uri; must be in the pool's callback allow-list. */
  redirectUri: string
  /** Where the Hosted-UI /logout returns the browser; in the pool's logout list. */
  logoutRedirectUri: string
  /** Orovia API origin (same var the preview/flags planes use). `null` if unset. */
  apiBase: string | null
  /** OAuth scopes requested at /authorize. */
  scope: string
}

type AuthEnv = Pick<
  ImportMetaEnv,
  | 'VITE_AUTH_DOMAIN'
  | 'VITE_AUTH_CLIENT_ID'
  | 'VITE_AUTH_REDIRECT_URI'
  | 'VITE_AUTH_LOGOUT_REDIRECT_URI'
  | 'VITE_OROVIA_API_BASE'
>

// Non-functional placeholders — real values arrive via deploy env. Kept obvious
// so a misconfigured deploy fails loudly at Cognito rather than looking wired.
const DEV_DEFAULTS = {
  authDomain: 'https://orovia-dev.auth.us-east-1.amazoncognito.com',
  clientId: 'REPLACE_WITH_COGNITO_APP_CLIENT_ID',
  redirectUri: 'http://localhost:5180/',
  logoutRedirectUri: 'http://localhost:5180/',
} as const

const OROVIA_SCOPE = 'openid email profile'

function trimTrailingSlash(value: string): string {
  return value.replace(/\/$/, '')
}

export function getAuthConfig(env: AuthEnv = import.meta.env): AuthConfig {
  const apiBase = env.VITE_OROVIA_API_BASE?.replace(/\/$/, '') || null
  return {
    authDomain: trimTrailingSlash(env.VITE_AUTH_DOMAIN || DEV_DEFAULTS.authDomain),
    clientId: env.VITE_AUTH_CLIENT_ID || DEV_DEFAULTS.clientId,
    redirectUri: env.VITE_AUTH_REDIRECT_URI || DEV_DEFAULTS.redirectUri,
    logoutRedirectUri:
      env.VITE_AUTH_LOGOUT_REDIRECT_URI ||
      env.VITE_AUTH_REDIRECT_URI ||
      DEV_DEFAULTS.logoutRedirectUri,
    apiBase,
    scope: OROVIA_SCOPE,
  }
}

/**
 * True only when a real login flow can run: a Hosted-UI domain, an app client
 * id, AND an API base to mint the session against are all configured. Local dev
 * with no auth env resolves false, so the standalone viewer renders un-gated
 * (existing behaviour) and only a deployed standalone requires login.
 */
export function isAuthConfigured(env: AuthEnv = import.meta.env): boolean {
  return Boolean(
    env.VITE_AUTH_DOMAIN &&
      env.VITE_AUTH_CLIENT_ID &&
      env.VITE_OROVIA_API_BASE,
  )
}

/** Build the Hosted-UI `/oauth2/authorize` URL for an auth-code + PKCE start. */
export function authorizeUrl(
  config: AuthConfig,
  params: { state: string; codeChallenge: string },
): string {
  const query = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scope,
    state: params.state,
    code_challenge: params.codeChallenge,
    code_challenge_method: 'S256',
  })
  return `${config.authDomain}/oauth2/authorize?${query.toString()}`
}

/** The Hosted-UI token endpoint for the authorization-code exchange. */
export function tokenEndpoint(config: AuthConfig): string {
  return `${config.authDomain}/oauth2/token`
}

/** Build the Hosted-UI `/logout` URL that clears the Cognito session cookie. */
export function logoutUrl(config: AuthConfig): string {
  const query = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: config.logoutRedirectUri,
  })
  return `${config.authDomain}/logout?${query.toString()}`
}
